// Modules
import { useApolloClient } from '@apollo/client'
import { useLogInfoStore } from 'redesign/store'
import {
    AUTH_BIOMETRY_MUTATION,
    CHANGE_PASSWORD_UNICO_MUTATION,
    CREATE_DRAFT_FACIAL_BIOMETRY_COMTACTS,
    CreateBiometryUrl,
    UPDATE_CONTACT_BIOMETRY_MUTATION,
} from 'services/gql/unico/mutation'
import { CheckBiometryResult, CheckUpdateContactBiometryLimit } from 'services/gql/unico/query'
import {
    checkBiometryResultType,
    checkUpdateContactBiometryLimitType,
    ContactsBiometry,
    ContactTypeEnum,
    createBiometryUrlType,
    createDraftFacialBiometryContactsData,
    updateContactBiometryData,
} from 'typings/unico'

export const useUnicoService = () => {
    const client = useApolloClient()
    const { logInfo } = useLogInfoStore()

    const createBiometryUrl = async (federalTaxId: string, callbackUri: string): Promise<createBiometryUrlType> => {
        const { data } = await client.mutate({
            mutation: CreateBiometryUrl,
            variables: {
                federalTaxId,
                callbackUri,
            },
        })

        return data as createBiometryUrlType
    }

    const getCheckBiometryResult = async (federalTaxId: string, unicoId: string) => {
        const {
            data: { checkBiometryResult },
        } = await client.query<checkBiometryResultType>({
            query: CheckBiometryResult,
            variables: {
                federalTaxId,
                unicoId,
            },
        })

        return checkBiometryResult
    }

    const updateContactsBiometry = async (federalTaxId: string, unicoId: string): Promise<updateContactBiometryData> => {
        const variables = {
            federalTaxId,
            unicoId,
        }

        const { data } = await client.mutate({
            mutation: UPDATE_CONTACT_BIOMETRY_MUTATION,
            variables,
        })

        return data
    }

    const createDraftFacialBiometryContacts = async (
        contacts: ContactsBiometry,
        uniqueId: string,
    ): Promise<createDraftFacialBiometryContactsData> => {
        const variables = {
            newContacts: [
                {
                    contactType: ContactTypeEnum.EMAIL,
                    principal: true,
                    value: contacts.email,
                },
                {
                    contactType: ContactTypeEnum.PHONE,
                    principal: true,
                    value: contacts.cellphone,
                },
            ],
            uniqueId,
        }

        const { data } = await client.mutate({
            mutation: CREATE_DRAFT_FACIAL_BIOMETRY_COMTACTS,
            variables,
        })

        return data
    }

    const authBiometry = async (federalTaxId: string, unicoId: string) => {
        const { data } = await client.mutate({
            mutation: AUTH_BIOMETRY_MUTATION,
            variables: {
                federalTaxId,
                unicoId,
                logInfo,
            },
        })
        return data
    }

    const changePasswordUnico = async (username: string, unicoId: string, newPassword: string) => {
        const { data } = await client.mutate({
            mutation: CHANGE_PASSWORD_UNICO_MUTATION,
            variables: {
                newPassword,
                unicoId,
                username,
            },
        })
        return data
    }

    const checkUpdateContactBiometryLimit = async (federalTaxId: string) => {
        const { data } = await client.query<checkUpdateContactBiometryLimitType>({
            query: CheckUpdateContactBiometryLimit,
            variables: {
                federalTaxId,
            },
        })
        return data
    }

    return {
        createBiometryUrl,
        getCheckBiometryResult,
        updateContactsBiometry,
        authBiometry,
        changePasswordUnico,
        checkUpdateContactBiometryLimit,
        createDraftFacialBiometryContacts,
    }
}
